import { makeAutoObservable, runInAction } from "mobx";
import api from "src/core/axios";

export interface OnboardingFilters {
    page: number;
    limit: number;
    status?: string;
    metaStatus?: string;
    search?: string;
}

export interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export class WhatsAppOnboardingStore {
    // State
    onboardingList: any[] = [];
    currentOnboarding: any = null;
    isFetchingList = false;
    isFetchingDetail = false;
    isDeleting = false;
    isSubmitting = false;

    pagination: PaginationInfo = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    };

    filters: OnboardingFilters = {
        page: 1,
        limit: 10,
        status: "",
        metaStatus: "",
        search: "",
    };

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    // Actions
    setFilters(newFilters: Partial<OnboardingFilters>) {
        this.filters = { ...this.filters, ...newFilters };
    }

    resetFilters() {
        this.filters = {
            page: 1,
            limit: 10,
            status: "",
            metaStatus: "",
            search: "",
        };
    }

    async fetchOnboardingList() {
        this.isFetchingList = true;

        try {
            const params = new URLSearchParams();
            params.append("page", this.filters.page.toString());
            params.append("limit", this.filters.limit.toString());

            if (this.filters.status) {
                params.append("status", this.filters.status);
            }

            if (this.filters.metaStatus) {
                params.append("metaStatus", this.filters.metaStatus);
            }

            if (this.filters.search) {
                params.append("search", this.filters.search);
            }

            const response = await api.get(`/whatsapp-onboarding?${params.toString()}`);

            // Handle the nested data structure
            const responseData = response.data;
            const items = responseData.data?.items || responseData.items || [];
            const pagination = responseData.data?.pagination || responseData.pagination || {
                page: 1,
                limit: 10,
                total: 0,
                pages: 0,
            };

            runInAction(() => {
                this.onboardingList = items;
                this.pagination = {
                    page: pagination.page || 1,
                    limit: pagination.limit || 10,
                    total: pagination.total || 0,
                    totalPages: pagination.pages || pagination.totalPages || 0,
                };
                this.isFetchingList = false;
            });
        } catch (error) {
            console.error("Failed to fetch onboarding list:", error);
            runInAction(() => {
                this.isFetchingList = false;
            });
        }
    }

    async fetchOnboardingById(id: string) {
        this.isFetchingDetail = true;

        try {
            const response = await api.get(`/whatsapp-onboarding/${id}`);

            // The data is nested under response.data.data
            const onboardingData = response.data?.data || response.data;

            runInAction(() => {
                this.currentOnboarding = onboardingData;
                this.isFetchingDetail = false;
            });

            return onboardingData;
        } catch (error) {
            console.error("Failed to fetch onboarding detail:", error);
            runInAction(() => {
                this.isFetchingDetail = false;
            });
            throw error;
        }
    }

    async deleteOnboarding(id: string) {
        this.isDeleting = true;

        try {
            await api.delete(`/whatsapp-onboarding/${id}`);

            runInAction(() => {
                this.isDeleting = false;
                // Remove from list if present
                this.onboardingList = this.onboardingList.filter(item => item.id !== id);
            });

            return true;
        } catch (error) {
            console.error("Failed to delete onboarding:", error);
            runInAction(() => {
                this.isDeleting = false;
            });
            throw error;
        }
    }

    async submitOnboarding(data: any) {
        this.isSubmitting = true;

        try {
            const response = await api.post("/whatsapp-onboarding", data);

            runInAction(() => {
                this.isSubmitting = false;
            });

            return response.data;
        } catch (error) {
            console.error("Failed to submit onboarding:", error);
            runInAction(() => {
                this.isSubmitting = false;
            });
            throw error;
        }
    }
}